// rollups-web.ts — the WEB runtime's overview aggregation.
//
// 🔴 WHY THIS EXISTS: `compute_overview` was a zero stub in web-commands.ts — every figure came
// back 0 and every list empty, so on the web surface the app showed nothing at all: no totals, no
// categories, no recurring charges. Native is unaffected (Rust computes it).
//
// Every rule below mirrors `src-tauri/src/rollups.rs`. Where the two could drift, the Rust wins —
// it is the shipping surface. If you change a threshold here, change it there.
//
// 2026-08-19: `build_query_slice` is now mirrored too (sliceForWindowWeb + resolveWindowWeb at
// the bottom) — leaving it stubbed meant the "Ask your spending" panel answered every question
// with "No transactions in ." on web.

export interface WebTxn {
  posted_on: string;
  merchant: string;
  raw_description: string;
  amount: number;   // MINOR units; negative = money out
  currency: string;
  category: string;
}

const money = (minor: number) => minor / 100;
const round2 = (v: number) => Math.round(v * 100) / 100;
const days = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

/** Mirror of rollups.rs::dominant_currency — the most common currency in the ledger. */
function dominantCurrency(txns: WebTxn[], fallback = 'GBP'): string {
  const c = new Map<string, number>();
  for (const t of txns) if (t.currency) c.set(t.currency, (c.get(t.currency) ?? 0) + 1);
  let best = fallback, n = 0;
  for (const [k, v] of c) if (v > n) { best = k; n = v; }
  return best;
}

/** Mirror of rollups.rs::median (integer minor units). */
function median(xs: number[]): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
}

/** Spend per category (money OUT only), biggest first. */
function categoryTotals(txns: WebTxn[]) {
  const acc = new Map<string, { spend: number; count: number }>();
  for (const t of txns) {
    if (t.amount >= 0) continue;
    const e = acc.get(t.category) ?? { spend: 0, count: 0 };
    e.spend += -t.amount; e.count += 1;
    acc.set(t.category, e);
  }
  return [...acc.entries()]
    .map(([category, e]) => ({ category, spend: round2(money(e.spend)), txn_count: e.count }))
    .sort((a, b) => b.spend - a.spend);
}

/** Spend per merchant with its most-common category, biggest first. */
function merchantTotals(txns: WebTxn[], limit = 15) {
  const acc = new Map<string, { spend: number; count: number; display: string }>();
  const cats = new Map<string, Map<string, number>>();
  for (const t of txns) {
    if (t.amount >= 0 || !t.merchant.trim()) continue;
    const k = t.merchant.toLowerCase();
    const e = acc.get(k) ?? { spend: 0, count: 0, display: t.merchant };
    e.spend += -t.amount; e.count += 1;
    acc.set(k, e);
    const cm = cats.get(k) ?? new Map<string, number>();
    cm.set(t.category, (cm.get(t.category) ?? 0) + 1);
    cats.set(k, cm);
  }
  return [...acc.entries()]
    .map(([k, e]) => {
      let category = 'uncategorized', n = 0;
      for (const [c, v] of cats.get(k) ?? []) if (v > n) { category = c; n = v; }
      return { merchant: e.display, spend: round2(money(e.spend)), txn_count: e.count, category };
    })
    .sort((a, b) => b.spend - a.spend)
    .slice(0, limit);
}

/** Spend and income per YYYY-MM, oldest first. */
function monthTotals(txns: WebTxn[]) {
  const acc = new Map<string, { spend: number; income: number; count: number }>();
  for (const t of txns) {
    const m = t.posted_on.slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(m)) continue;
    const e = acc.get(m) ?? { spend: 0, income: 0, count: 0 };
    if (t.amount < 0) e.spend += -t.amount; else e.income += t.amount;
    e.count += 1;
    acc.set(m, e);
  }
  return [...acc.entries()]
    .map(([month, e]) => ({ month, spend: round2(money(e.spend)), income: round2(money(e.income)), txn_count: e.count }))
    .sort((a, b) => a.month.localeCompare(b.month));
}

/** Mirror of rollups.rs::detect_recurring — a merchant charged ≥3 times where MOST consecutive
 *  gaps fall in a cadence band (weekly 5–9d, monthly 26–35d). Amount = median of the charges.
 *  A merchant without a clear cadence is NOT flagged: guessing "recurring" on an irregular
 *  merchant would put a wrong number in front of someone's money. */
function detectRecurring(txns: WebTxn[]) {
  const byMerchant = new Map<string, WebTxn[]>();
  for (const t of txns) {
    if (t.amount >= 0 || !t.merchant.trim()) continue;
    const k = t.merchant.toLowerCase();
    byMerchant.set(k, [...(byMerchant.get(k) ?? []), t]);
  }
  const out: {
    merchant: string; category: string; typical_amount: number; occurrences: number;
    cadence: string; first_seen: string; last_seen: string;
  }[] = [];
  for (const group of byMerchant.values()) {
    if (group.length < 3) continue;
    const g = [...group].sort((a, b) => a.posted_on.localeCompare(b.posted_on));
    const gaps: number[] = [];
    for (let i = 1; i < g.length; i++) gaps.push(days(g[i - 1].posted_on, g[i].posted_on));
    const monthly = gaps.filter((x) => x >= 26 && x <= 35).length;
    const weekly = gaps.filter((x) => x >= 5 && x <= 9).length;
    let cadence: string;
    if (monthly * 2 >= gaps.length) cadence = 'monthly';
    else if (weekly * 2 >= gaps.length) cadence = 'weekly';
    else continue;                                  // no clear cadence => not flagged
    const typical = median(g.map((t) => -t.amount));
    out.push({
      merchant: g[0].merchant,
      category: g[0].category,
      typical_amount: round2(money(typical)),
      occurrences: g.length,
      cadence,
      first_seen: g[0].posted_on,
      last_seen: g[g.length - 1].posted_on,
    });
  }
  // Rust sorts by typical_amount desc. The UI re-ranks by ANNUALISED cost (see RecurringCard);
  // keeping this order identical to the Rust means both surfaces hand the UI the same input.
  return out.sort((a, b) => b.typical_amount - a.typical_amount);
}

/** Mirror of rollups.rs::overview. */
export function computeOverviewWeb(txns: WebTxn[], today: string) {
  const dates = txns.map((t) => t.posted_on).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)).sort();
  const total_spend = txns.filter((t) => t.amount < 0).reduce((s, t) => s + -t.amount, 0);
  const total_income = txns.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  return {
    schema:
      'oriora.statements.v1-overview: derived aggregates only (no raw rows). All figures in major units of `currency`. Spend = money out; income = money in.',
    generated_for: today,
    currency: dominantCurrency(txns),
    total_txns: txns.length,
    date_range: dates.length ? ([dates[0], dates[dates.length - 1]] as [string, string]) : null,
    total_spend: round2(money(total_spend)),
    total_income: round2(money(total_income)),
    by_category: categoryTotals(txns),
    top_merchants: merchantTotals(txns, 15),
    by_month: monthTotals(txns),
    recurring: detectRecurring(txns),
  };
}

// ── The ASK slice (mirror of rollups.rs::slice_for_window + ipc.rs::resolve_window) ──────────
// 🔴 WHY: build_query_slice was the LAST zero stub — the "Ask your spending" panel answered
// every question with "No transactions in ." (empty label, no window) on web, measured live
// 2026-08-19. Same rule as above: where this and the Rust could drift, the Rust wins.

// Local mirrors of categorize.rs::category_label + rollups.rs::fmt_money/currency_symbol —
// duplicated HERE (not imported from store.ts) to avoid a web-runtime import cycle; the Rust
// side duplicates them for the same reason.
const CAT_LABELS: Record<string, string> = {
  groceries: 'Groceries', dining: 'Dining & takeaway', transport: 'Transport', fuel: 'Fuel',
  shopping: 'Shopping', utilities: 'Utilities & bills', rent_mortgage: 'Rent / mortgage',
  subscriptions: 'Subscriptions', entertainment: 'Entertainment', health: 'Health & fitness',
  travel: 'Travel', cash: 'Cash', fees: 'Fees & charges', income: 'Income', transfers: 'Transfers',
  uncategorized: 'Uncategorized',
};
const catLabel = (c: string) => CAT_LABELS[c] ?? (c ? c.charAt(0).toUpperCase() + c.slice(1) : 'Uncategorized');
const curSym = (code: string) =>
  ({ GBP: '£', USD: '$', AUD: '$', CAD: '$', NZD: '$', EUR: '€', JPY: '¥' } as Record<string, string>)[code.toUpperCase()] ?? '';
function fmtMoney(value: number, currency: string): string {
  const neg = value < 0;
  const abs = Math.abs(value);
  const whole = Math.trunc(abs);
  const cents = Math.round((abs - whole) * 100);
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${neg ? '-' : ''}${curSym(currency)}${grouped}.${String(cents).padStart(2, '0')}`;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);
const parseIso = (s: string) => (/^\d{4}-\d{2}-\d{2}/.test(s) ? new Date(`${s.slice(0, 10)}T00:00:00Z`) : null);
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const firstOfMonth = (y: number, m: number) => new Date(Date.UTC(y, m - 1, 1));
const lastOfMonth = (y: number, m: number) => addDays(m === 12 ? firstOfMonth(y + 1, 1) : firstOfMonth(y, m + 1), -1);
const monthLabel = (y: number, m: number) => `${MONTH_NAMES[m - 1]} ${y}`;

function extractLastNDays(q: string): number | null {
  const tokens = q.split(/\s+/);
  for (let i = 0; i + 2 < tokens.length; i++) {
    if ((tokens[i] === 'last' || tokens[i] === 'past') && tokens[i + 2].startsWith('day')) {
      const n = Number(tokens[i + 1]);
      if (Number.isInteger(n) && n > 0 && n <= 3650) return n;
    }
  }
  return null;
}

/** Mirror of ipc.rs::resolve_window. Returns [fromDate, toDate, label]. */
export function resolveWindowWeb(question: string, from: string | undefined, to: string | undefined, today: Date): [Date, Date, string] {
  if (from && to) {
    const fd = parseIso(from), td = parseIso(to);
    if (fd && td) return [fd, td, `${from} to ${to}`];
  }
  const q = question.toLowerCase();
  const y = today.getUTCFullYear(), m = today.getUTCMonth() + 1;
  if (q.includes('this month')) return [firstOfMonth(y, m), today, monthLabel(y, m)];
  if (q.includes('last month') || q.includes('previous month')) {
    const [py, pm] = m === 1 ? [y - 1, 12] : [y, m - 1];
    return [firstOfMonth(py, pm), lastOfMonth(py, pm), monthLabel(py, pm)];
  }
  if (q.includes('this year') || q.includes('year to date') || q.includes('ytd'))
    return [firstOfMonth(y, 1), today, `${y} (year to date)`];
  if (q.includes('last year')) return [firstOfMonth(y - 1, 1), lastOfMonth(y - 1, 12), String(y - 1)];
  const n = extractLastNDays(q);
  if (n != null) return [addDays(today, -(n - 1)), today, `last ${n} days`];
  for (let mi = 0; mi < 12; mi++) {
    if (q.includes(MONTH_NAMES[mi].toLowerCase())) {
      const mm = mi + 1;
      const yy = mm <= m ? y : y - 1;
      return [firstOfMonth(yy, mm), lastOfMonth(yy, mm), monthLabel(yy, mm)];
    }
  }
  return [addDays(today, -89), today, 'last 90 days'];
}

/** Mirror of rollups.rs::slice_for_window. */
export function sliceForWindowWeb(all: WebTxn[], question: string, from: Date, to: Date, windowLabel: string) {
  const currency = dominantCurrency(all);
  const inWindow = (txns: WebTxn[], f: Date, t: Date) =>
    txns.filter((x) => { const d = parseIso(x.posted_on); return d != null && d >= f && d <= t; });
  const windowTxns = inWindow(all, from, to);

  const spanDays = Math.round((to.getTime() - from.getTime()) / 86400000);
  const priorTo = addDays(from, -1);
  const priorFrom = addDays(priorTo, -spanDays);
  const priorTxns = inWindow(all, priorFrom, priorTo);

  const curCats = categoryTotals(windowTxns);
  const priorCats = categoryTotals(priorTxns);
  const priorMap = new Map(priorCats.map((c) => [c.category, c.spend]));
  const mom = curCats
    .map((c) => {
      const prior = priorMap.get(c.category) ?? 0;
      const delta = round2(c.spend - prior);
      return {
        category: c.category, current: c.spend, prior, delta,
        pct_change: prior > 0 ? round2(((c.spend - prior) / prior) * 100) : null,
        direction: delta > 0.5 ? 'up' : delta < -0.5 ? 'down' : 'flat',
      };
    })
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 8);

  const spendMinor = windowTxns.filter((t) => t.amount < 0).reduce((s, t) => s + -t.amount, 0);
  const incomeMinor = windowTxns.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const priorSpendMinor = priorTxns.filter((t) => t.amount < 0).reduce((s, t) => s + -t.amount, 0);
  const window_spend = round2(money(spendMinor));
  const window_income = round2(money(incomeMinor));
  const window_net = round2(money(incomeMinor - spendMinor));
  const prior_window_spend = round2(money(priorSpendMinor));
  const spend_delta = round2(window_spend - prior_window_spend);
  const spend_pct_change = prior_window_spend > 0 ? round2(((window_spend - prior_window_spend) / prior_window_spend) * 100) : null;
  const spend_direction = spend_delta > 0.5 ? 'up' : spend_delta < -0.5 ? 'down' : 'flat';
  const avg_spend_per_day = round2(window_spend / Math.max(spanDays + 1, 1));

  const top_merchants = merchantTotals(windowTxns, 12);
  const recurring = detectRecurring(all);
  const activeRecurring = recurring.filter((r) => { const d = parseIso(r.last_seen); return d != null && d >= from && d <= to; });
  const recurring_in_window = activeRecurring.length;
  const recurring_monthly_total = round2(
    activeRecurring.reduce((acc, r) => acc + (r.cadence === 'monthly' ? r.typical_amount : r.cadence === 'weekly' ? r.typical_amount * 4.33 : 0), 0),
  );

  // headline facts — 1:1 with rollups.rs::build_headline_facts
  const mny = (v: number) => fmtMoney(v, currency);
  const facts: { kind: string; text: string; tone: string }[] = [];
  const windowTxnCount = windowTxns.length;
  if (windowTxnCount > 0) {
    if (prior_window_spend > 0 && spend_direction !== 'flat') {
      const word = spend_direction === 'up' ? 'up' : 'down';
      const pctPart = spend_pct_change != null ? ` (${spend_pct_change > 0 ? '+' : ''}${Math.round(spend_pct_change)}%)` : '';
      facts.push({ kind: 'total', tone: word, text: `You spent ${mny(window_spend)} across ${windowTxnCount} transactions in ${windowLabel} — ${word} ${mny(Math.abs(spend_delta))}${pctPart} from the prior period.` });
    } else {
      facts.push({ kind: 'total', tone: 'neutral', text: `You spent ${mny(window_spend)} across ${windowTxnCount} transactions in ${windowLabel}.` });
    }
    const topC = curCats[0];
    if (topC) {
      const share = window_spend > 0 ? Math.round((topC.spend / window_spend) * 100) : 0;
      facts.push({ kind: 'top_category', tone: 'neutral', text: `${catLabel(topC.category)} was the biggest area at ${mny(topC.spend)} (${share}% of spending, ${topC.txn_count} transactions).` });
    }
    const mv = mom.find((d) => d.direction !== 'flat' && d.prior > 0);
    if (mv) {
      const word = mv.direction === 'up' ? 'up' : 'down';
      const pctPart = mv.pct_change != null ? ` (${mv.pct_change > 0 ? '+' : ''}${Math.round(mv.pct_change)}%)` : '';
      facts.push({ kind: 'mover', tone: word, text: `${catLabel(mv.category)} changed the most — ${word} ${mny(Math.abs(mv.delta))}${pctPart}, from ${mny(mv.prior)} to ${mny(mv.current)}.` });
    }
    const topM = top_merchants[0];
    if (topM) {
      facts.push({ kind: 'merchant', tone: 'neutral', text: `Your most-spent merchant was ${topM.merchant} at ${mny(topM.spend)} across ${topM.txn_count} ${topM.txn_count === 1 ? 'visit' : 'visits'}.` });
    }
    if (recurring_in_window > 0) {
      const names = activeRecurring.slice(0, 3).map((r) => r.merchant);
      const namePart = names.length ? ` — including ${names.join(', ')}` : '';
      facts.push(recurring_monthly_total > 0
        ? { kind: 'recurring', tone: 'neutral', text: `${recurring_in_window} recurring charge${recurring_in_window === 1 ? '' : 's'} are running, about ${mny(recurring_monthly_total)}/month combined${namePart}.` }
        : { kind: 'recurring', tone: 'neutral', text: `${recurring_in_window} recurring charge${recurring_in_window === 1 ? '' : 's'} were active in this period${namePart}.` });
    }
    if (window_income > 0) {
      const netWord = window_net >= 0 ? 'more in than out' : 'more out than in';
      facts.push({ kind: 'income', tone: window_net >= 0 ? 'down' : 'up', text: `Money in was ${mny(window_income)} versus ${mny(window_spend)} out — ${mny(Math.abs(window_net))} ${netWord} over the period.` });
    }
  }

  return {
    schema:
      'oriora.statements.v2-slice: PRE-COMPUTED aggregates for ONE question. All math already done on-device; narrate these figures only — do NOT recompute, estimate, or invent numbers. Figures in major units of `currency`. Spend = money out (positive figure); income = money in. mom_deltas compare each category to the equally-long PRIOR window (positive delta = spent more). headline_facts = pre-ranked, ready-to-say observational sentences already containing the figures — base the narration on THESE, lead with the most informative. HARD CEILING: state only facts, totals, deltas, and what was spent. NEVER give financial, budgeting, investment, or tax advice; NEVER predict or forecast; NEVER say what the owner should do.',
    question: question.trim(),
    currency,
    window: [iso(from), iso(to)] as [string, string],
    window_label: windowLabel,
    window_txn_count: windowTxnCount,
    window_spend, window_income, window_net,
    prior_window_spend,
    prior_window_txn_count: priorTxns.length,
    spend_delta, spend_pct_change, spend_direction,
    avg_spend_per_day,
    by_category: curCats,
    top_merchants,
    by_month: monthTotals(windowTxns),
    mom_deltas: mom,
    recurring,
    recurring_in_window,
    recurring_monthly_total,
    headline_facts: facts,
  };
}
